/**
 * The intake session — the one owner of "this subject is being fixed for
 * World" (ADR-040). It takes World's hand-off at boot (only on a click), runs
 * the armature check on the file's bytes, and, on the intake export, re-checks
 * the bytes it wrote and leaves them for World. No automation: every step here
 * starts from the person's click (the prompt, the opt-in button, the export).
 *
 * The session decides nothing about the stage: the file reaches the stage
 * through the ONE load door (`load`, ADR-032), exactly like a drop, and the
 * export body comes from the export door's producer (`exportBody`). It holds
 * only its own state, and every collaborator is injected.
 *
 * FAIL LOUD: every storage failure, refusal and cancel is a status line
 * (`note`); the fallback is the plain manual drop, and the line says so.
 *
 * Depends on: handoff-store, intake-view (types + plannedFixes), kernel report
 * types. Depended on by: main.ts, shell/intake-panel.ts, tests.
 */
import { withFindings, type RigCheckFinding, type RigCheckReport } from '@harryhausen/kernel';
import {
  GLB_MIME,
  consumeWorldHandoff,
  readWorldHandoff,
  withdrawHarryHandoff,
  writeHarryHandoff,
  type HandoffBackend,
  type HandoffClock,
} from './handoff-store.js';
import { FLOW_START, intakeView, type IntakeFlow, type IntakeViewState } from './intake-view.js';

export const WORLD_UPLOAD_PATH = '/world/';

/** The Fix step's build (INTAKE_GUIDED_FLOW §3.4 D): the export door's body + World's canon, on the click, no save dialog. */
export interface IntakeBuilt {
  readonly glb: ArrayBuffer;
  readonly filename: string;
  /** For the record and the status line (renamed 31 bones to World's skeleton, re-seated the frames (median 124.7°)). */
  readonly fixes: readonly string[];
  /** The same, in the person's words, for "What changed" (Renamed 31 bones. Straightened the arms and legs.). */
  readonly receipts: readonly string[];
}

/** What the guided flow's buttons ask of the session (the frame maps the screen's actions here). */
export type FlowAction = 'next' | 'keep-hands' | 'add-fingers' | 'fingers-done' | 'let-sway' | 'keep-still' | 'grow-tail' | 'tail-done' | 'tail-leave' | 'fix-again' | 'see-walk' | 'retry-walk' | 'back' | 'send' | 'save-copy';

/** The intake export's body, produced by the export door; `write` lands the file the person chose. */
export interface IntakeExportBody {
  readonly glb: ArrayBuffer;
  readonly filename: string;
  /** What the export changed, in plain words (renamed 31 bones, re-seated frames median 124.7°). */
  readonly fixes: readonly string[];
  /** Write the file where the person chose; answers a suffix for the status line (" (downloaded instead)"), or throws. */
  write(): Promise<string>;
}

export interface IntakeSessionDeps {
  readonly backend: HandoffBackend;
  readonly clock: HandoffClock;
  readonly check: (bytes: ArrayBuffer, label: string) => Promise<RigCheckReport>;
  /** The one prompt: resolve true on Load, false on Not now. Names and reasons are display only. */
  readonly ask: (offer: { readonly name: string; readonly reasons: readonly string[]; readonly ageMs: number }) => Promise<boolean>;
  /**
   * The ONE load door (ADR-032), as a drop walks it, under the sequence the Load CLICK reserved;
   * true = the subject is on stage.
   */
  readonly load: (file: File, seq: number) => Promise<boolean>;
  /** Reserve the load order at the gesture (the door's #140 rule) — called on the Load click, before any await. */
  readonly reserve: () => number;
  /** Is this reserved load still the newest gesture? A later drop or open wins. */
  readonly isCurrent: (seq: number) => boolean;
  /** The subject on stage: its Document uri and label, and the bytes it was loaded from when held. */
  readonly subject: () => { readonly uri: string; readonly label: string; readonly bytes: ArrayBuffer | null };
  /** The status line. `err` reveals it (a refusal). */
  readonly note: (line: string, err?: unknown) => void;
  /** The page asked for the hand-off (`?handoff=1`), and the way to strip that from the URL. */
  readonly url: { readonly asked: () => boolean; readonly strip: () => void };
  readonly harrySha: string;
  /** This page session's writer id, minted once per page load — marks the return records it may withdraw. */
  readonly writerId: string;
  /** Produce the intake export: null = the person cancelled the save. */
  readonly exportBody: (report: RigCheckReport | null) => Promise<IntakeExportBody | null>;
  /** The Body tab's World check section, opened when a session starts. */
  readonly reveal: () => void;
  /**
   * The page asked for the hand-off (`?handoff=1`) and found nothing usable (none, stale, tampered,
   * blocked): the guided frame's nothing-waiting screen. The reason still goes to `note`.
   */
  readonly missed?: (reason: string) => void;
  /** Build the fixed file for the Fix step: the export door's producer + World's canon, no save dialog. */
  readonly build?: (report: RigCheckReport) => Promise<IntakeBuilt>;
  /** Which questions this body asks, on the stage as it stands (fingers: no digits to pin; tail: a rigged tail that can sway). */
  readonly questions?: (report: RigCheckReport) => { readonly fingers: boolean; readonly tail: boolean; readonly tailUnrigged?: boolean };
  /** Let the tail sway (the tail spring ON) — the C2 answer. Throws with its refusal. */
  readonly setTailSway?: (on: boolean) => void;
  /** Play World's walk on the built bytes in the isolated proof stage; resolves when it plays, or why not. */
  readonly showWalk?: (glb: ArrayBuffer) => Promise<{ readonly ok: true } | { readonly ok: false; readonly reason: string }>;
  /** Put the proof stage away. */
  readonly hideWalk?: () => void;
  /**
   * Save a copy of the built file (the picker, then the anchor download): resolves true when written,
   * false when the person cancelled the picker (F5: a cancel decides nothing). Throws with its reason.
   */
  readonly saveCopy?: (glb: ArrayBuffer, filename: string) => Promise<boolean>;
  /**
   * The person World has signed in on this origin (ADR-224 §4.3's `auth_user`, paired with `auth_token`),
   * for a session that did NOT start from World's upload (Allen: the manual path returns too). Display and
   * matching only — it stamps the record's `userId`, which World compares with its own signed-in person.
   */
  readonly worldUser?: () => { readonly id: string; readonly name: string | null } | null;
  /** The name World shows for the sent avatar (the person's own, else the tidied file name). Display only. */
  readonly displayName?: () => string;
  /** Say on the `poqpoq-handoff` channel that a record was written (world-2d); optional, never throws. */
  readonly announce?: (message: { readonly sha256: string; readonly size: number; readonly displayName: string; readonly at: string; readonly worldTab?: string }) => void;
  /**
   * What only the MESH can say, measured on the subject once it is on stage (a tail the skeleton lacks —
   * `tail-unrigged`). Added to the bytes' report; the verdict is recomputed.
   */
  readonly meshFindings?: () => readonly RigCheckFinding[];
  /**
   * C2b, "Give it bones": grow the offered tail (ADR-031 T2) with its spring on, and open its editor — the
   * person then sees it wag, changes it, and says Done (or Leave it). Nothing is kept without that answer.
   */
  readonly growTail?: () => Promise<void>;
  /** C2b, "Leave it" in the tail's editor: take back what Give it bones grew (the chain, its zone, the spring). */
  readonly leaveTail?: () => Promise<void>;
}

interface WorldOrigin {
  readonly kind: 'world';
  readonly name: string;
  readonly reasons: readonly string[];
  readonly userId: string | null;
  /** World's tab nonce (World #2280), copied unchanged into the return record and the ping. */
  readonly worldTab?: string;
}

export interface IntakeSession {
  readonly active: () => boolean;
  readonly state: () => IntakeViewState | null;
  readonly subscribe: (listener: () => void) => () => void;
  /** At boot: read World's hand-off and, if there is one, ask. */
  readonly boot: () => Promise<void>;
  /** The person opts in on the subject already on stage. */
  readonly optIn: () => Promise<void>;
  /** The stage changed subject: a different file is re-checked; the same one is left alone. */
  readonly subjectChanged: () => void;
  readonly exportForWorld: () => Promise<void>;
  /** The guided flow's buttons: move, answer, fix, check. */
  readonly act: (action: FlowAction) => Promise<void>;
  /** The record this session last left for World (its sha), or null — Back to World asks the channel about it. */
  readonly lastSent: () => { readonly sha256: string } | null;
  readonly close: () => void;
}

const reasonOf = (err: unknown): string => (err instanceof Error ? err.message.split('\n')[0] ?? err.message : String(err));

/** A File name the load door routes as a model: World's name, made a `.glb` if it is not one (display text only). */
export function handoffFileName(name: string): string {
  const base = name.replace(/[\\/]/g, '_').trim() || 'avatar.glb';
  return /\.(glb|vrm)$/i.test(base) ? base : `${base}.glb`;
}

export function createIntakeSession(deps: IntakeSessionDeps): IntakeSession {
  let state: IntakeViewState | null = null;
  let origin: WorldOrigin | { kind: 'opt-in' } | null = null;
  /**
   * WHICH FILE the report is about, by identity: the held bytes themselves (every load holds a
   * fresh buffer, so a same-named replacement is a different file — Codex P1, #238), or the uri
   * when the stage holds no bytes (the seed). `undefined` = not tracking (a hand-off mid-load).
   */
  let checked: ArrayBuffer | string | undefined;
  const identityOf = (s: { readonly uri: string; readonly bytes: ArrayBuffer | null }): ArrayBuffer | string => s.bytes ?? `uri:${s.uri}`;
  let generation = 0;
  let exporting = false;
  /** The sha256 of every harry-to-world record THIS session wrote — the only ones it may withdraw. */
  const written = new Set<string>();
  const listeners = new Set<() => void>();
  const set = (next: IntakeViewState | null): void => {
    state = next;
    listeners.forEach((l) => l());
  };
  const viewOrigin = (): IntakeViewState['origin'] =>
    origin === null || origin.kind === 'opt-in' ? { kind: 'opt-in' } : { kind: 'world', name: origin.name, reasons: origin.reasons };

  /** Check bytes for the given subject; stale results (a newer check started) are dropped. */
  const runCheck = async (bytes: ArrayBuffer, label: string, onStage: boolean): Promise<RigCheckReport | null> => {
    const mine = ++generation;
    // A new check is a new flow: whatever was built or walking belonged to the previous file.
    built = null;
    buildGeneration += 1;
    lastSent = null;
    deps.hideWalk?.();
    set({ origin: viewOrigin(), checking: true, error: null, report: null, onStage, exported: null });
    try {
      const checkedReport = await deps.check(bytes, label);
      if (mine !== generation) return null;
      const report = onStage ? augmented(checkedReport) : checkedReport;
      set({ origin: viewOrigin(), checking: false, error: null, report, onStage, exported: null });
      const evidence = report.findings.filter((f) => f.severity !== 'info' || f.code === 'frames-unmeasured').map((f) => `${f.code}: ${f.evidence}`);
      deps.note(`World check — ${label}: ${report.verdict}${evidence.length > 0 ? ` · ${evidence.join(' · ')}` : ''}`);
      return report;
    } catch (err) {
      if (mine !== generation) return null;
      set({ origin: viewOrigin(), checking: false, error: reasonOf(err), report: null, onStage, exported: null });
      deps.note(`World check — ${label} could not be checked: ${reasonOf(err)}`, err);
      return null;
    }
  };

  const accept = async (offer: { name: string; reasons: readonly string[]; userId: string | null; worldTab?: string; bytes: ArrayBuffer }, seq: number): Promise<void> => {
    const mine: WorldOrigin = { kind: 'world', name: offer.name, reasons: offer.reasons, userId: offer.userId, ...(offer.worldTab !== undefined ? { worldTab: offer.worldTab } : {}) };
    origin = mine;
    /** The person closed the World check (or opted in afresh) while this hand-off awaited: it is no longer the session's. */
    const superseded = (): boolean => origin !== mine;
    checked = undefined;
    deps.reveal();
    const fileName = handoffFileName(offer.name);
    /** The stage when the hand-off began: anything else on it later arrived while `checked` was not tracking. */
    const stageAtAccept = identityOf(deps.subject());
    const report = await runCheck(offer.bytes, fileName, false);
    // A closed World check stays closed: no line, no tracking, no re-check (Codex P2, #239 round 3).
    if (superseded()) return;
    // Every path that leaves World's file OFF the stage resumes tracking the subject that IS on it, so
    // the advertised recovery ("drop the file here") is re-checked (Codex P2, #238 round 2) — and a
    // subject that reached the stage WHILE the hand-off was pending (its subjectChanged was ignored)
    // is checked now, not merely marked as checked (Codex P2, #239). Called after the path's own
    // line, so the new check speaks last.
    const trackStage = (): void => {
      const s = deps.subject();
      checked = identityOf(s);
      if (checked === stageAtAccept) return;
      generation += 1;
      if (s.bytes === null) {
        set({ origin: viewOrigin(), checking: false, error: `the bytes of ${s.label} are not held in this session — drop the file to check it`, report: null, onStage: true, exported: null });
        return;
      }
      void runCheck(s.bytes, s.label, true);
    };
    if (report === null) {
      deps.note(`World's file is still waiting — Harry could not check it; reload to try again, or drop the file here`);
      trackStage();
      return;
    }
    if (report.findings.some((f) => f.code === 'no-skeleton')) {
      // Nothing to put on stage, and nothing in V1 changes that: the hand-off is used up.
      await consumeQuietly();
      deps.url.strip();
      // The consume is an await too: a World check closed during it stays closed (Codex P2, #239 round 4).
      if (superseded()) return;
      trackStage();
      return;
    }
    // The check is slow; a drop or open made meanwhile is a NEWER gesture and wins (Codex P2, #238).
    if (!deps.isCurrent(seq)) {
      session.close();
      deps.note(`World's file is still waiting — you opened another file while Harry was checking it; reload to load World's file`);
      return;
    }
    const loaded = await deps.load(new File([offer.bytes], fileName, { type: GLB_MIME }), seq);
    if (superseded()) {
      // The load was the person's click and it landed; the hand-off is used — but the closed check stays closed.
      if (loaded) {
        await consumeQuietly();
        deps.url.strip();
      }
      return;
    }
    if (!loaded) {
      deps.note(`World's file is still waiting — the load was refused (see above); reload to try again, or drop the file here`, new Error('handoff load refused'));
      // Checked but refused at the load door: a refusal the card can show, never an endless "Opening…" (Codex P2, #241).
      if (state !== null) set({ ...state, error: "Harry checked the file but couldn't put it on the stage — the import refused it. World's file is still waiting; reload to try again.", onStage: false });
      trackStage();
      return;
    }
    checked = identityOf(deps.subject());
    if (state !== null) set({ ...state, onStage: true, report: state.report === null ? null : augmented(state.report) });
    await consumeQuietly();
    deps.url.strip();
  };

  const consumeQuietly = async (): Promise<void> => {
    try {
      await consumeWorldHandoff(deps.backend);
    } catch (err) {
      deps.note(`loaded, but World's hand-off could not be cleared (${reasonOf(err)}) — Harry will offer it again until it expires`, err);
    }
  };

  // ─── the guided flow (INTAKE_GUIDED_FLOW §3.7): step, answers, the build, the walk ───
  /** The Fix step's bytes — never in the view state (the card names what changed, never carries a file). */
  let built: (IntakeBuilt & { readonly subject: ArrayBuffer | string }) | null = null;
  let buildGeneration = 0;
  let lastSent: { sha256: string } | null = null;
  /**
   * Who the return record is for — ONE rule for both doors (guided Send and the header's Export): a World
   * record's userId as it came (null stays nobody's, Codex P2 #243); the signed-in fallback only for a
   * session with no World record.
   */
  const returnUser = (): { userId: string | null; signedIn: { readonly id: string; readonly name: string | null } | null } => {
    if (origin?.kind === 'world') return { userId: origin.userId, signedIn: null };
    const signedIn = deps.worldUser?.() ?? null;
    return { userId: signedIn?.id ?? null, signedIn };
  };
  /**
   * Leave a return record for World — the ONE write for both doors (Codex P2, #244): write, remember it as
   * this session's, and when it is for a World person, make it the one Back to World asks about and announce
   * it on the world-2d channel.
   */
  const leaveForWorld = async (input: { name: string; bytes: ArrayBuffer; fixes: readonly string[]; userId: string | null }) => {
    const displayName = deps.displayName?.() ?? '';
    // World #2280: the tab that pressed Open in Harry, copied unchanged (a manual session has none).
    const worldTab = origin?.kind === 'world' ? origin.worldTab : undefined;
    const record = await writeHarryHandoff(
      deps.backend,
      { name: input.name, bytes: input.bytes, harrySha: deps.harrySha, fixes: input.fixes, userId: input.userId, writer: deps.writerId, displayName, ...(worldTab !== undefined ? { worldTab } : {}) },
      deps.clock,
    );
    written.add(record.sha256);
    if (record.userId !== null) {
      lastSent = { sha256: record.sha256 };
      deps.announce?.({ sha256: record.sha256, size: record.size, displayName: record.displayName ?? displayName, at: record.createdAt, ...(record.worldTab !== undefined ? { worldTab: record.worldTab } : {}) });
    }
    return record;
  };
  /** The bytes' report plus what only the mesh can say (when the subject is on stage). */
  const augmented = (report: RigCheckReport): RigCheckReport => withFindings(report, deps.meshFindings?.() ?? []);
  const flowOf = (): IntakeFlow => state?.flow ?? FLOW_START;
  const patchFlow = (patch: Partial<IntakeFlow>): IntakeFlow => {
    const next = { ...flowOf(), ...patch };
    if (state !== null) set({ ...state, flow: next });
    return next;
  };
  /**
   * The order (Allen's rerun, 2026-10-03): What's wrong → the FIXES (built and re-checked, "Fixed" says
   * what Harry did) → the optional questions, fingers then the tail LAST → Check. An answer that changed
   * the body (fingers added, a tail given bones, a sway turned on or off) rebuilds before Check, so the
   * walk and the record carry it; an answer that changed nothing goes straight back to Fixed.
   */
  let edited = false;
  const advance = async (from: IntakeFlow): Promise<void> => {
    const report = state?.report;
    if (report === null || report === undefined) return;
    if (from.step === 'wrong') {
      patchFlow({ questions: deps.questions?.(report) ?? { fingers: false, tail: false } });
      await fix();
      return;
    }
    const flow = flowOf();
    if (flow.questions.fingers && flow.answers.fingers === null) {
      patchFlow({ step: 'ask-fingers' });
      return;
    }
    if (flow.questions.tail && flow.answers.tail === null) {
      patchFlow({ step: 'ask-tail' });
      return;
    }
    if (edited || built === null) {
      await fix();
      return;
    }
    patchFlow({ step: 'fix' });
  };
  /**
   * D, on the click: build (the export door's body + World's canon, no save dialog), then the re-check on
   * the built bytes. Bound to the subject at the click (the #239 capture): another character reaching the
   * stage stops it (F4); a re-check that still refuses is F2, and any record this session left earlier is
   * withdrawn (ADR-040 §7).
   */
  const fix = async (): Promise<void> => {
    const report = state?.report;
    if (report === null || report === undefined || deps.build === undefined) return;
    const mine = ++buildGeneration;
    built = null;
    lastSent = null;
    edited = false;
    deps.hideWalk?.();
    // A guided fix supersedes the header Export's result: the steps own the card again (Codex P2, #245 —
    // `exported` outranks the flow in the resolver, so leaving it would pin the card on that result).
    if (state !== null && state.exported !== null) set({ ...state, exported: null });
    patchFlow({ step: 'fix', build: { status: 'building' }, walk: { status: 'idle' }, send: { status: 'idle' }, saved: false });
    const subjectAtClick = identityOf(deps.subject());
    if (checked !== subjectAtClick) {
      patchFlow({ build: { status: 'stage-changed' } });
      return;
    }
    const stale = (): boolean => mine !== buildGeneration || state === null;
    try {
      const body = await deps.build(report);
      if (stale()) return;
      if (identityOf(deps.subject()) !== subjectAtClick) {
        patchFlow({ build: { status: 'stage-changed' } });
        return;
      }
      const after = await deps.check(body.glb, body.filename);
      if (stale()) return;
      if (identityOf(deps.subject()) !== subjectAtClick) {
        patchFlow({ build: { status: 'stage-changed' } });
        return;
      }
      const blocking = after.findings.filter((f) => f.severity === 'needs-harry' || f.severity === 'unsupported');
      if (blocking.length > 0) {
        let withdrawn = '';
        try {
          if (await withdrawHarryHandoff(deps.backend, { writer: deps.writerId, shas: written })) withdrawn = ' · the earlier export this session left for World was withdrawn';
        } catch (err) {
          withdrawn = ` · an earlier export this session left for World could NOT be withdrawn (${reasonOf(err)})`;
        }
        // The withdraw is an await too: a newer check or subject must not receive this file's refusal (Codex P2, #242).
        if (stale() || identityOf(deps.subject()) !== subjectAtClick) return;
        deps.note(`fix for World: the re-check still says ${after.verdict}: ${blocking.map((f) => `${f.code} (${f.evidence})`).join(' · ')}${withdrawn}`);
        patchFlow({ build: { status: 'refused', reasons: blocking.map((f) => f.message) } });
        return;
      }
      built = { ...body, subject: subjectAtClick };
      deps.note(`fixed for World — ${body.fixes.length > 0 ? body.fixes.join(', ') : 'nothing needed changing'} · re-check ${after.verdict}`);
      patchFlow({ build: { status: 'built', receipts: body.receipts } });
    } catch (err) {
      if (stale()) return;
      deps.note(`fix for World: ${reasonOf(err)}`, err);
      patchFlow({ build: { status: 'failed', reason: reasonOf(err) } });
    }
  };
  /** E: World's walk on the built bytes, in the proof stage. The re-check is the gate; the walk is the eye. */
  const walk = async (): Promise<void> => {
    const bytes = built;
    if (bytes === null) return;
    const mine = buildGeneration;
    patchFlow({ step: 'check', walk: { status: 'loading' } });
    if (deps.showWalk === undefined) {
      patchFlow({ walk: { status: 'unavailable', reason: 'this Harry has no proof stage' } });
      return;
    }
    const shown = await deps.showWalk(bytes.glb);
    if (mine !== buildGeneration || state === null || flowOf().step !== 'check') return;
    patchFlow({ walk: shown.ok ? { status: 'playing' } : { status: 'unavailable', reason: shown.reason } });
  };

  /**
   * F, Send back to World (design §3.4 F; ADR-040 §2 as amended): the record is written from the bytes the
   * Fix step built and re-checked, and Check showed — no rebuild, no save dialog. F4 when another file
   * reached the stage since; F3 when no World person can be named; F1 when storage refuses.
   */
  const send = async (): Promise<void> => {
    const b = built;
    if (b === null) return;
    if (identityOf(deps.subject()) !== b.subject || checked !== b.subject) {
      patchFlow({ send: { status: 'stage-changed' } });
      return;
    }
    // The signed-in fallback is for a session with NO World record only (Codex P2, #243): a World record whose
    // userId is null stays nobody's — never stamped for whoever is signed in by Send time.
    const { userId, signedIn } = returnUser();
    if (userId === null) {
      patchFlow({ send: { status: 'no-user' } });
      deps.note('send for World: no World person to hand it to — this session did not start from World\'s upload and nobody is signed in to World here; save the file and upload it in World');
      return;
    }
    const mine = buildGeneration;
    patchFlow({ send: { status: 'sending' } });
    try {
      // world-2d: a World tab that can take it will answer; the return reads that answer.
      await leaveForWorld({ name: b.filename, bytes: b.glb, fixes: b.fixes, userId });
      if (mine !== buildGeneration || state === null) return;
      patchFlow({ send: { status: 'sent' } });
      deps.note(`sent for World — ${b.filename}${signedIn !== null ? ` (for the person signed in to World here${signedIn.name !== null ? `, ${signedIn.name}` : ''})` : ''} · ${b.fixes.length > 0 ? b.fixes.join(', ') : 'nothing needed changing'} · go back to World's avatar upload to use it`);
    } catch (err) {
      if (mine !== buildGeneration || state === null) return;
      patchFlow({ send: { status: 'blocked', reason: reasonOf(err) } });
      deps.note(`send for World: storage refused (${reasonOf(err)}) — save the file and upload it in World`, err);
    }
  };
  /** Save a copy (optional after Send; the way through F1 and F3). A cancel decides nothing (F5). */
  const saveCopy = async (): Promise<void> => {
    const b = built;
    if (b === null || deps.saveCopy === undefined) return;
    try {
      const saved = await deps.saveCopy(b.glb, b.filename);
      if (saved) {
        if (state !== null) patchFlow({ saved: true });
        deps.note(`saved a copy: ${b.filename}`);
      }
    } catch (err) {
      deps.note(`save a copy: ${reasonOf(err)}`, err);
    }
  };

  const session: IntakeSession = {
    lastSent: () => lastSent,
    active: () => state !== null,
    state: () => state,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    boot: async () => {
      const asked = deps.url.asked();
      const read = await readWorldHandoff(deps.backend, deps.clock);
      if (read.kind === 'none') {
        if (asked) {
          deps.note("World's hand-off: nothing is waiting — drop your file here instead");
          deps.missed?.('nothing is waiting');
        }
        return;
      }
      if (read.kind === 'unavailable') {
        deps.note(`World's hand-off could not be read (${read.reason}) — drop your file here instead`, new Error(read.reason));
        if (asked) deps.missed?.(read.reason);
        return;
      }
      if (read.kind === 'ignored') {
        deps.note(`World's hand-off was ignored: ${read.reason} — drop your file here instead`, new Error(read.reason));
        if (asked) deps.missed?.(read.reason);
        return;
      }
      const yes = await deps.ask({ name: read.record.name, reasons: read.record.verdict.reasons, ageMs: read.ageMs });
      if (!yes) {
        deps.note("World's file is still waiting. Reopen Harry from World to fix it.");
        return;
      }
      // The click IS the gesture: its place in the load order is taken now, before the check's await.
      const seq = deps.reserve();
      await accept({ name: read.record.name, reasons: read.record.verdict.reasons, userId: read.record.userId, ...(read.record.worldTab !== undefined ? { worldTab: read.record.worldTab } : {}), bytes: read.bytes }, seq);
    },
    optIn: async () => {
      origin = { kind: 'opt-in' };
      deps.reveal();
      const s = deps.subject();
      checked = identityOf(s);
      if (s.bytes === null) {
        set({ origin: viewOrigin(), checking: false, error: `the bytes of ${s.label} are not held in this session — drop the file to check it`, report: null, onStage: true, exported: null });
        return;
      }
      await runCheck(s.bytes, s.label, true);
    },
    subjectChanged: () => {
      if (state === null || origin === null) return;
      const s = deps.subject();
      if (checked === undefined || identityOf(s) === checked) return;
      checked = identityOf(s);
      generation += 1; // an in-flight check of the previous subject must not land on this one
      if (s.bytes === null) {
        set({ origin: viewOrigin(), checking: false, error: `the bytes of ${s.label} are not held in this session — drop the file to check it`, report: null, onStage: true, exported: null });
        return;
      }
      void runCheck(s.bytes, s.label, true);
    },
    exportForWorld: async () => {
      if (state === null || exporting) return;
      // The top bar's Export routes here too: a stop (no skeleton, a VRM) or an unchecked file has
      // nothing to send — and the stage may still hold the PREVIOUS character, which must never leave as this one.
      const view = intakeView(state);
      if (!view.canExport || state.report === null) {
        deps.note(`nothing to export for World — ${view.headline} Close the World check to export the stage as usual.`, new Error('intake export refused'));
        return;
      }
      // EVERYTHING the export is about is fixed at the CLICK (Codex #238 round 2): which file the report
      // describes, the report, and who it goes back to in World. A drop during the export's awaits
      // changes the stage, never this export's subject; a close mid-export does not strip the World
      // identity — the export the person started finishes as started (close does not cancel it).
      const subjectAtClick = identityOf(deps.subject());
      if (checked !== subjectAtClick) {
        deps.note('nothing exported for World — the character on stage is not the one Harry checked; wait for its check, then export', new Error('intake export: subject not checked'));
        return;
      }
      const report = state.report;
      // The manual path returns too (Allen): no World record → the person signed in to World on this origin.
      const { userId } = returnUser();
      const sameSubject = (): boolean => identityOf(deps.subject()) === subjectAtClick;
      /**
       * THE RULE (Codex P1, #239): an export that ends saying "not left for World" also withdraws
       * any return record this session left EARLIER — World must never offer a file Harry just said
       * it didn't leave. Only this session's records (by sha256) are withdrawn; another session's
       * stays. A cancel withdraws nothing: nothing was decided.
       */
      const withdraw = async (): Promise<string> => {
        try {
          return (await withdrawHarryHandoff(deps.backend, { writer: deps.writerId, shas: written })) ? ' · the earlier export this session left for World was withdrawn' : '';
        } catch (err) {
          return ` · the earlier export this session left for World could NOT be withdrawn (${reasonOf(err)}) — World may still offer it`;
        }
      };
      const changed = async (when: string): Promise<void> => {
        const withdrawn = await withdraw();
        deps.note(`export for World stopped ${when} — another character reached the stage, so nothing was written and nothing left for World${withdrawn}; export again`, new Error('intake export: subject changed'));
      };
      exporting = true;
      try {
        const body = await deps.exportBody(report);
        if (body === null) {
          deps.note('export cancelled — nothing written, nothing left for World');
          return;
        }
        if (!sameSubject()) {
          await changed('while the file was being made');
          return;
        }
        const after = await deps.check(body.glb, body.filename);
        if (!sameSubject()) {
          await changed('while the file was being re-checked');
          return;
        }
        const fixes = body.fixes.length > 0 ? ` — ${body.fixes.join(', ')}` : '';
        const where = await body.write();
        const blocking = after.findings.filter((f) => f.severity === 'needs-harry' || f.severity === 'unsupported');
        const settle = (savedForWorld: boolean, card: string, line: string, err?: unknown): void => {
          if (state !== null) set({ ...state, exported: { savedForWorld, line: card } });
          deps.note(line, err);
        };
        if (blocking.length > 0) {
          const why = blocking.map((f) => f.message).join(' ');
          const withdrawn = await withdraw();
          settle(false, `Exported, but World would still refuse it: ${why}`, `exported ${body.filename}${where}${fixes} · NOT left for World — the re-check still says ${after.verdict}: ${blocking.map((f) => `${f.code} (${f.evidence})`).join(' · ')}${withdrawn}`, new Error('intake re-check failed'));
          return;
        }
        try {
          await leaveForWorld({ name: body.filename, bytes: body.glb, fixes: body.fixes, userId });
        } catch (err) {
          const withdrawn = await withdraw();
          settle(false, `Exported. Harry could not leave it for World (${reasonOf(err)}) — upload the file in World yourself.`, `exported ${body.filename}${where}${fixes} · could not leave it for World (${reasonOf(err)}) — upload the file in World yourself${withdrawn}`, err);
          return;
        }
        if (userId === null) {
          settle(
            false,
            "Exported. This file didn't come from World's upload, so World won't offer it back — upload it in World yourself.",
            `exported ${body.filename}${where}${fixes} · re-check ${after.verdict} · not offered to World: this session did not start from World's upload, so the file carries no World account — upload it in World yourself`,
          );
          return;
        }
        settle(true, "Saved for World — go back to World's avatar upload to use it.", `exported ${body.filename}${where}${fixes} · re-check ${after.verdict} · Saved for World — go back to World's avatar upload to use it (poqpoq.com${WORLD_UPLOAD_PATH})`);
      } catch (err) {
        // Reaches the card too: in the guided frame the status line is hidden (Codex P2, #241).
        if (state !== null) set({ ...state, exported: { savedForWorld: false, line: `Harry couldn't finish the export: ${reasonOf(err)}. Nothing was sent.` } });
        deps.note(`export for World: ${reasonOf(err)}`, err);
      } finally {
        exporting = false;
      }
    },
    act: async (action) => {
      if (state === null || state.report === null) return;
      const flow = flowOf();
      switch (action) {
        case 'next':
          return advance(flow);
        case 'keep-hands':
          return advance(patchFlow({ answers: { ...flow.answers, fingers: 'keep' } }));
        case 'add-fingers':
          patchFlow({ answers: { ...flow.answers, fingers: 'add' }, step: 'doing-fingers' });
          return;
        case 'fingers-done':
          edited = true;
          return advance(flowOf());
        case 'let-sway':
          try {
            deps.setTailSway?.(true);
          } catch (err) {
            deps.note(`tail spring: ${reasonOf(err)}`, err);
            return advance(patchFlow({ answers: { ...flow.answers, tail: 'still' } }));
          }
          edited = true;
          return advance(patchFlow({ answers: { ...flow.answers, tail: 'sway' } }));
        case 'keep-still':
          // A spring already on (set in the Full editor) is turned OFF: the export must match the answer (Codex P2, #243).
          if (!flow.questions.tailUnrigged) {
            try {
              deps.setTailSway?.(false);
              edited = true;
            } catch (err) {
              deps.note(`tail spring: ${reasonOf(err)}`, err);
            }
          }
          return advance(patchFlow({ answers: { ...flow.answers, tail: 'still' } }));
        case 'grow-tail':
          // C2b, unrigged: Give it bones OPENS the tail's editor with the bones grown and the wag playing —
          // the person sees it and changes it; Done keeps it, Leave it takes it back (Allen's rule).
          try {
            await deps.growTail?.();
          } catch (err) {
            deps.note(`tail: ${reasonOf(err)}`, err);
            return advance(patchFlow({ answers: { ...flow.answers, tail: 'still' } }));
          }
          edited = true;
          patchFlow({ step: 'doing-tail' });
          return;
        case 'tail-done':
          edited = true;
          return advance(patchFlow({ answers: { ...flow.answers, tail: 'sway' } }));
        case 'tail-leave':
          try {
            await deps.leaveTail?.();
          } catch (err) {
            deps.note(`tail: ${reasonOf(err)}`, err);
          }
          edited = true;
          return advance(patchFlow({ answers: { ...flow.answers, tail: 'still' } }));
        case 'fix-again':
          return fix();
        case 'see-walk':
        case 'retry-walk':
          return walk();
        case 'back': {
          // Back KEEPS the answers (Codex P2, #242): fingers grafted and a sway turned on are applied to the
          // Document, so asking again could claim a stiff tail or no fingers while the edits stand. Back goes
          // to What's wrong; Next rebuilds without re-asking. Changing an answer is the Full editor's.
          deps.hideWalk?.();
          built = null;
          buildGeneration += 1;
          patchFlow({ step: 'wrong', build: { status: 'none' }, walk: { status: 'idle' }, send: { status: 'idle' } });
          return;
        }
        case 'send':
          return send();
        case 'save-copy':
          return saveCopy();
      }
    },
    close: () => {
      generation += 1;
      buildGeneration += 1;
      origin = null;
      checked = undefined;
      built = null;
      lastSent = null;
      deps.hideWalk?.();
      set(null);
    },
  };
  return session;
}
